import { useEffect, useRef, useState } from "react";
import { Badge, Drawer, Layout, Tooltip } from "antd";
import {
  ApartmentOutlined,
  BellOutlined,
  CloudDownloadOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  LogoutOutlined,
  MenuOutlined,
  NodeIndexOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Dashboard      from "./pages/Dashboard";
import SensorDetail   from "./pages/SensorDetail";
import SFlowSources   from "./pages/SFlowSources";
import GraphsStorage  from "./pages/GraphsStorage";
import UserManagement from "./pages/UserManagement";
import Notifications  from "./pages/Notifications";
import Flows          from "./pages/Flows";
import Login          from "./pages/Login";
import ZoneEditor     from "./pages/ZoneEditor";
import IPAddresses    from "./pages/IPAddresses";
import Settings       from "./pages/Settings";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { IPZoneProvider } from "./contexts/IPZoneContext";
import SidebarIPBrowser from "./components/SidebarIPBrowser";
import RequireAuth    from "./components/RequireAuth";
import { api }        from "./api";

// ── sidebar nav config ────────────────────────────────────────────────────

interface NavItem {
  key:      string;
  icon:     React.ReactNode;
  label:    string;
  to:       string;
  disabled?: boolean;
}

interface NavSection {
  title:    string;
  items:    NavItem[];
}

const NAV: NavSection[] = [
  {
    title: "Reports",
    items: [
      { key: "/", icon: <DashboardOutlined />, label: "Sensors Dashboard", to: "/" },
      // /flows is intentionally hidden from the nav — access via direct URL /flows
    ],
  },
  {
    title: "Network",
    items: [
      { key: "/sensors", icon: <NodeIndexOutlined />, label: "sFlow Sources", to: "/sensors" },
      { key: "/zones",   icon: <ApartmentOutlined />, label: "IP Zones",      to: "/zones"   },
    ],
  },
  {
    title: "Settings",
    items: [
      { key: "/storage",       icon: <DatabaseOutlined />, label: "Graphs & Storage",  to: "/storage"       },
      { key: "/users",         icon: <UserOutlined />,     label: "User Management",   to: "/users"         },
      { key: "/notifications", icon: <BellOutlined />,     label: "Notifications",     to: "/notifications" },
      { key: "/settings",      icon: <SettingOutlined />,  label: "Settings & Updates", to: "/settings" },
    ],
  },
];

function activeKey(pathname: string): string {
  if (pathname === "/") return "/";
  if (pathname.startsWith("/sensors"))       return "/sensors";
  if (pathname.startsWith("/zones"))         return "/zones";
  if (pathname.startsWith("/flows"))         return "/flows";
  if (pathname.startsWith("/ip"))            return "/ip";
  if (pathname.startsWith("/storage"))       return "/storage";
  if (pathname.startsWith("/users"))         return "/users";
  if (pathname.startsWith("/notifications")) return "/notifications";
  if (pathname.startsWith("/settings"))     return "/settings";
  return "/";
}

// ── sidebar content ───────────────────────────────────────────────────────

function SidebarContent({ onNav }: { onNav?: () => void }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const current = activeKey(loc.pathname);

  async function handleLogout() {
    await logout();
    nav("/login", { replace: true });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>

      {/* Nav sections */}
      <nav className="sflow-nav">
        {NAV.map((section) => (
          <div key={section.title} className="sflow-nav-section">
            <span className="sflow-nav-section-title">{section.title}</span>

            {section.items.map((item) =>
              item.disabled ? (
                <span
                  key={item.key}
                  className="sflow-nav-item"
                  style={{ opacity: 0.35, cursor: "not-allowed" }}
                >
                  <span className="sflow-nav-icon">{item.icon}</span>
                  <span className="sflow-nav-label">{item.label}</span>
                </span>
              ) : (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`sflow-nav-item${current === item.key ? " active" : ""}`}
                  onClick={onNav}
                >
                  <span className="sflow-nav-icon">{item.icon}</span>
                  <span className="sflow-nav-label">{item.label}</span>
                </Link>
              )
            )}

            {/* Wansight-style IP Addresses + IP Groups panels, right
                under the Network section. */}
            {section.title === "Network" && (
              <SidebarIPBrowser onNav={onNav} />
            )}
          </div>
        ))}
      </nav>

      {/* User footer */}
      {user && (
        <div className="sflow-sidebar-footer">
          <div className="sflow-user-avatar" style={{ width: 28, height: 28, fontSize: 12, flexShrink: 0 }}>
            {user.username[0].toUpperCase()}
          </div>
          <div className="sflow-sidebar-footer-info">
            <div className="sflow-sidebar-footer-name">{user.username}</div>
            <div className="sflow-sidebar-footer-role">{user.role}</div>
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            className="sflow-sidebar-logout"
            aria-label="Logout"
          >
            <LogoutOutlined />
          </button>
        </div>
      )}
    </div>
  );
}

// ── shell ─────────────────────────────────────────────────────────────────

const UPDATE_POLL_INTERVAL = 60 * 60 * 1000; // 1 hour

function Shell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  async function handleShellLogout() {
    await logout();
    nav("/login", { replace: true });
  }
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [hasUpdate, setHasUpdate] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Poll for updates every hour (also on mount)
  useEffect(() => {
    const checkUpdate = async () => {
      try {
        const result = await api.updateCheck();
        setHasUpdate(result.has_update);
      } catch {
        // mirror unreachable — silently ignore
      }
    };
    checkUpdate();
    pollTimerRef.current = setInterval(checkUpdate, UPDATE_POLL_INTERVAL);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  return (
    <Layout style={{ minHeight: "100vh" }}>

      {/* ── Top header ─────────────────────────────────────────────────── */}
      <Layout.Header className="sflow-header">
        {isMobile && (
          <button className="sflow-hamburger" onClick={() => setDrawerOpen(true)}>
            <MenuOutlined />
          </button>
        )}

        <span className="sflow-brand">
          <span className="sflow-brand-icon" aria-hidden="true">
            <svg viewBox="0 0 64 64" width="18" height="18" fill="none">
              <path d="M12 24 Q 22 16, 32 24 T 52 24" stroke="#fff" strokeWidth="4" strokeLinecap="round" opacity="0.95"/>
              <path d="M12 34 Q 22 26, 32 34 T 52 34" stroke="#fff" strokeWidth="4" strokeLinecap="round" opacity="0.75"/>
              <path d="M12 44 Q 22 36, 32 44 T 52 44" stroke="#fff" strokeWidth="4" strokeLinecap="round" opacity="0.55"/>
            </svg>
          </span>
          <span className="sflow-brand-text">LeAnh Console</span>
        </span>

        <div className="sflow-header-spacer" />

        {/* Update available badge */}
        {hasUpdate && (
          <Tooltip title="Update available — click to view">
            <button
              onClick={() => nav("/settings")}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: 6,
                color: "#60a5fa",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
              }}
            >
              <Badge dot>
                <CloudDownloadOutlined style={{ fontSize: 16 }} />
              </Badge>
              {!isMobile && <span style={{ marginLeft: 6 }}>Update</span>}
            </button>
          </Tooltip>
        )}

        {user && !isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className="sflow-user-chip">
              <div className="sflow-user-avatar">
                {user.username[0].toUpperCase()}
              </div>
              <span className="sflow-user-name">{user.username}</span>
              <span className="sflow-user-role">{user.role}</span>
            </div>
            <Tooltip title="Logout">
              <button
                onClick={handleShellLogout}
                className="sflow-logout-btn"
                aria-label="Logout"
              >
                <LogoutOutlined />
                <span className="sflow-logout-text">Logout</span>
              </button>
            </Tooltip>
          </div>
        )}

        {user && isMobile && (
          <Tooltip title="Logout">
            <button
              onClick={handleShellLogout}
              className="sflow-logout-btn"
              aria-label="Logout"
            >
              <LogoutOutlined />
            </button>
          </Tooltip>
        )}
      </Layout.Header>

      <Layout style={{ flex: 1, minHeight: 0 }}>

        {/* ── Desktop sidebar ─────────────────────────────────────────── */}
        {!isMobile && (
          <Layout.Sider
            className="sflow-sider"
            width={220}
            style={{
              background: "#0f172a",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 48px)",
              position: "sticky",
              top: 48,
              alignSelf: "flex-start",
            }}
          >
            <SidebarContent />
          </Layout.Sider>
        )}

        {/* ── Mobile drawer ───────────────────────────────────────────── */}
        {isMobile && (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            placement="left"
            width={230}
            className="sflow-mobile-drawer"
            styles={{
              body:    { padding: 0, background: "#0f172a", display: "flex", flexDirection: "column" },
              header:  { display: "none" },
              content: { background: "#0f172a" },
              wrapper: { background: "#0f172a" },
              mask:    { backdropFilter: "blur(3px)" },
            }}
          >
            <SidebarContent onNav={() => setDrawerOpen(false)} />
          </Drawer>
        )}

        {/* ── Main content ────────────────────────────────────────────── */}
        <Layout style={{ background: "#f8fafc", minWidth: 0 }}>
          <Layout.Content style={{ padding: isMobile ? 12 : 20, minHeight: "calc(100vh - 48px)" }}>
            <Routes>
              <Route path="/"             element={<Dashboard />}      />
              <Route path="/sensors"      element={<SFlowSources />}   />
              <Route path="/sensors/:id"  element={<SensorDetail />}   />
              <Route path="/zones"        element={<ZoneEditor />}      />
              <Route path="/storage"      element={<GraphsStorage />}  />
              <Route path="/users"        element={<UserManagement />} />
              <Route path="/flows"        element={<Flows />}          />
              <Route path="/ip"           element={<IPAddresses />}    />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/settings"     element={<Settings />}       />
            </Routes>
          </Layout.Content>

          <Layout.Footer className="sflow-footer">
            <span>LeAnh Console v0.1</span>
            <span style={{ color: "#cbd5e1" }}>{window.location.host}</span>
          </Layout.Footer>
        </Layout>

      </Layout>
    </Layout>
  );
}

// ── root ──────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <IPZoneProvider>
                <Shell />
              </IPZoneProvider>
            </RequireAuth>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
